import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import type { AxiosError } from 'axios'
import { cn } from '../UI/cn'
import {
  getLatestActionSummary,
  recordActionClick,
  updateActionClickSentiment,
  ActionSummaryData,
  EnrichedAction,
  SignalType,
  ActionTypeEnum,
  SentimentType,
} from '../../services/action_summary_api'
import { SignalSection } from './SignalSection'
import { CompanySection } from './CompanySection'
import { SummaryStatsHeader } from './SummaryStatsHeader'
import { SIGNAL_SECTIONS } from './constants'
import { Tabs } from '../FbUI/Tabs'

/** Maximum time to poll for summary generation before giving up (5 minutes) */
const POLLING_TIMEOUT_MS = 5 * 60 * 1000

type ViewMode = 'signal' | 'company'

interface ActionSummaryCardProps {
  className?: string
  /** Optional user ID to view another user's summary (superusers only) */
  userId?: number
  /** Callback fired whenever the summary changes */
  onSummaryLoaded?: (summary: ActionSummaryData | null) => void
}

/**
 * Displays AI-generated daily action summary for the current user.
 *
 * Shows prioritized action items grouped by either:
 * - Signal type: unreplied_email, inactive_deal
 * - Company: grouped by contact company
 *
 * Superusers can pass a userId prop to view another user's summary.
 */
export const ActionSummaryCard: React.FC<ActionSummaryCardProps> = ({
  className,
  userId,
  onSummaryLoaded,
}) => {
  const [viewMode, setViewMode] = useState<ViewMode>('signal')
  const viewTabs = useMemo(
    () => [
      { key: 'signal' as ViewMode, title: 'Signal type' },
      { key: 'company' as ViewMode, title: 'Company' },
    ],
    []
  )
  // Whether each signal section is expanded (showing its actions)
  const [signalSectionExpanded, setSignalSectionExpanded] = useState<
    Record<string, boolean>
  >({
    unreplied_email: true,
    inactive_deal: true,
  })
  // Whether to show all actions within a section (vs first 3)
  const [expandedSections, setExpandedSections] = useState<
    Record<string, boolean>
  >({
    unreplied_email: false,
    inactive_deal: false,
  })
  const [expandedCompanies, setExpandedCompanies] = useState<
    Record<number, boolean>
  >({})

  const summaryQuery = useQuery<ActionSummaryData | null, Error>({
    queryKey: ['action-summary', userId ?? 'self'],
    queryFn: async () => {
      try {
        return await getLatestActionSummary(userId)
      } catch (err) {
        const axiosError = err as AxiosError<{ detail?: string }>
        if (axiosError.response?.status === 404) {
          return null
        }
        throw new Error(
          axiosError.response?.data?.detail ||
            axiosError.message ||
            'Failed to fetch action summary'
        )
      }
    },
    refetchOnWindowFocus: false,
  })

  const summary = summaryQuery.data

  // Track when we started polling (to implement timeout)
  const [pollingStartTime, setPollingStartTime] = useState<number | null>(null)

  // Poll every 3 seconds while generating, with 5-minute timeout
  useEffect(() => {
    if (summary?.status === 'GENERATING') {
      // Track when polling started
      if (pollingStartTime === null) {
        setPollingStartTime(Date.now())
      }

      const interval = setInterval(() => {
        // Stop polling after timeout to prevent infinite loops
        if (
          pollingStartTime &&
          Date.now() - pollingStartTime > POLLING_TIMEOUT_MS
        ) {
          console.warn(
            `Action summary generation polling timed out after ${POLLING_TIMEOUT_MS / 1000 / 60} minutes`
          )
          clearInterval(interval)
          return
        }
        summaryQuery.refetch()
      }, 3000)
      return () => clearInterval(interval)
    } else {
      // Reset polling start time when not generating
      if (pollingStartTime !== null) {
        setPollingStartTime(null)
      }
    }
  }, [summary?.status, summaryQuery.refetch, pollingStartTime, userId])

  useEffect(() => {
    onSummaryLoaded?.(summary ?? null)
  }, [summary, onSummaryLoaded])

  const toggleSignalSection = (sectionKey: string) => {
    setSignalSectionExpanded((prev) => ({
      ...prev,
      [sectionKey]: !prev[sectionKey],
    }))
  }

  const toggleSection = (sectionKey: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionKey]: !prev[sectionKey],
    }))
  }

  const toggleCompany = (companyId: number) => {
    setExpandedCompanies((prev) => ({
      ...prev,
      [companyId]: !prev[companyId],
    }))
  }

  // Compute actions_by_signal from enriched_actions if not provided by backend
  const actionsBySignal = useMemo(() => {
    const backendGrouping = summary?.summary_data?.actions_by_signal
    // Prefer backend-computed grouping if available
    if (backendGrouping && Object.keys(backendGrouping).length > 0) {
      return backendGrouping
    }

    // Fallback: compute from enriched_actions
    if (!summary?.summary_data?.enriched_actions) return {}

    const grouped: Record<string, EnrichedAction[]> = {}
    for (const action of summary.summary_data.enriched_actions) {
      const signalType = action.signal_type
      if (signalType) {
        if (!grouped[signalType]) {
          grouped[signalType] = []
        }
        grouped[signalType].push(action)
      }
    }
    return grouped
  }, [
    summary?.summary_data?.actions_by_signal,
    summary?.summary_data?.enriched_actions,
  ])

  // Group actions by company for company view
  const actionsByCompany = useMemo(() => {
    if (!summary?.summary_data?.enriched_actions) return []

    const grouped = summary.summary_data.enriched_actions.reduce(
      (acc, action) => {
        const companyId = action.contact_company_id
        if (!acc[companyId]) {
          acc[companyId] = {
            companyId,
            companyName: action.company_name,
            actions: [],
          }
        }
        acc[companyId].actions.push(action)
        return acc
      },
      {} as Record<
        number,
        { companyId: number; companyName: string; actions: EnrichedAction[] }
      >
    )

    // Sort companies by total urgency (most urgent first)
    return Object.values(grouped).sort((a, b) => {
      // Prioritize companies with unreplied emails
      const aHasEmail = a.actions.some(
        (act) => act.signal_type === 'unreplied_email'
      )
      const bHasEmail = b.actions.some(
        (act) => act.signal_type === 'unreplied_email'
      )
      if (aHasEmail && !bHasEmail) return -1
      if (bHasEmail && !aHasEmail) return 1

      // Then by number of actions
      return b.actions.length - a.actions.length
    })
  }, [summary?.summary_data?.enriched_actions])

  // Derive initial maps from existing user_clicks
  const { sentimentByAction, initialClickIdMap } = useMemo(() => {
    const sentimentMap = new Map<string, SentimentType>()
    const idMap = new Map<string, number>()
    if (summary?.user_clicks) {
      for (const click of summary.user_clicks) {
        const key = `${click.deal_id}-${click.signal_type}`
        idMap.set(key, click.click_id)
        if (click.sentiment) {
          sentimentMap.set(key, click.sentiment)
        }
      }
    }
    return { sentimentByAction: sentimentMap, initialClickIdMap: idMap }
  }, [summary?.user_clicks])

  // Track click IDs by (deal_id, signal_type) for sentiment updates.
  // Seeded from server data, then updated as the user creates new clicks.
  const [clickIdMap, setClickIdMap] = useState<Map<string, number>>(new Map())

  useEffect(() => {
    setClickIdMap(initialClickIdMap)
  }, [initialClickIdMap])

  // Helper to get existing sentiment for an action
  const getSentimentForAction = useCallback(
    (action: EnrichedAction): SentimentType | null => {
      const key = `${action.deal_id}-${action.signal_type}`
      return sentimentByAction.get(key) ?? null
    },
    [sentimentByAction]
  )

  const handleActionClick = useCallback(
    async (
      action: EnrichedAction,
      signalType: SignalType,
      actionType: ActionTypeEnum
    ) => {
      if (!summary || !summary.id) return

      // Record the click asynchronously (fire and forget)
      try {
        const response = await recordActionClick({
          action_summary_id: summary.id,
          signal_type: signalType,
          action_type: actionType,
          deal_id: action.deal_id,
          contact_company_id: action.contact_company_id,
        })
        // Store click_id so sentiment updates use PATCH instead of creating duplicates
        if (response?.click_id) {
          const key = `${action.deal_id}-${signalType}`
          setClickIdMap((prev) => new Map(prev).set(key, response.click_id))
        }
      } catch (err) {
        // Silently fail - click tracking shouldn't block navigation
        console.error('Failed to record action click:', err)
      }
    },
    [summary]
  )

  const handleSentimentClick = useCallback(
    async (
      action: EnrichedAction,
      signalType: SignalType,
      actionType: ActionTypeEnum,
      sentiment: SentimentType
    ) => {
      if (!summary || !summary.id) return

      const key = `${action.deal_id}-${signalType}`
      const existingClickId = clickIdMap.get(key)

      try {
        if (existingClickId) {
          // Update existing click record via PATCH (no duplicate)
          await updateActionClickSentiment({
            click_id: existingClickId,
            sentiment,
          })
        } else {
          // No prior click — create a new record with sentiment
          const response = await recordActionClick({
            action_summary_id: summary.id,
            signal_type: signalType,
            action_type: actionType,
            deal_id: action.deal_id,
            contact_company_id: action.contact_company_id,
            sentiment,
          })
          if (response?.click_id) {
            setClickIdMap((prev) => new Map(prev).set(key, response.click_id))
          }
        }
      } catch (err) {
        // Silently fail - sentiment tracking shouldn't block UI
        console.error('Failed to record sentiment:', err)
      }
    },
    [summary, clickIdMap]
  )

  if (summaryQuery.isLoading) {
    return (
      <div
        className={cn(
          'rounded-lg border border-gray-200 bg-white p-6 shadow-sm',
          className
        )}
      >
        <div className="animate-pulse space-y-4">
          <div className="h-6 w-1/3 rounded bg-gray-200"></div>
          <div className="space-y-2">
            <div className="h-4 w-full rounded bg-gray-200"></div>
            <div className="h-4 w-5/6 rounded bg-gray-200"></div>
            <div className="h-4 w-4/6 rounded bg-gray-200"></div>
          </div>
        </div>
      </div>
    )
  }

  const queryError = summaryQuery.error as Error | undefined

  if (queryError) {
    return (
      <div
        className={cn(
          'rounded-lg border border-red-200 bg-red-50 p-6',
          className
        )}
      >
        <div className="flex items-center gap-2 text-red-800">
          <svg
            className="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <span className="font-medium">Error loading action summary</span>
        </div>
        <p className="mt-2 text-sm text-red-700">{queryError.message}</p>
      </div>
    )
  }

  // No summary exists yet (status is null)
  if (!summary || summary.status === null) {
    return (
      <div
        className={cn(
          'rounded-lg border border-gray-200 bg-white p-6 shadow-sm',
          className
        )}
      >
        <div className="text-center">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
            />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            No action summary available
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            Click &quot;Generate Summary&quot; to create your daily action plan
          </p>
        </div>
      </div>
    )
  }

  if (summary.status === 'FAILED') {
    return (
      <div
        className={cn(
          'rounded-lg border border-yellow-200 bg-yellow-50 p-6',
          className
        )}
      >
        <div className="flex items-center gap-2 text-yellow-800">
          <svg
            className="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <span className="font-medium">Summary generation failed</span>
        </div>
        {summary.error_message && (
          <p className="mt-2 text-sm text-yellow-700">
            {summary.error_message}
          </p>
        )}
      </div>
    )
  }

  if (summary.status === 'GENERATING') {
    return (
      <div
        className={cn(
          'rounded-lg border border-gray-200 bg-white p-6',
          className
        )}
      >
        <div className="flex items-center gap-2 text-gray-700">
          <svg className="h-5 w-5 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
          <span className="font-medium">Generating your action summary...</span>
        </div>
        <p className="mt-2 text-sm text-gray-600">
          This usually takes 10-30 seconds
        </p>
      </div>
    )
  }

  const hasActions =
    summary.summary_data?.enriched_actions &&
    summary.summary_data.enriched_actions.length > 0

  // Check if we have signal-based data available
  const hasSignalData = Object.keys(actionsBySignal).length > 0

  return (
    <div
      className={cn(
        'rounded-lg border border-gray-200 bg-white shadow-sm overflow-hidden',
        className
      )}
    >
      {hasActions ? (
        <div className="bg-gray-50 px-4 py-4 space-y-4">
          {/* Summary Stats Header */}
          {summary.summary_data && (
            <SummaryStatsHeader data={summary.summary_data} />
          )}

          {/* View Mode Toggle */}
          {hasSignalData && (
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500">Group by:</span>
              <div className="flex-1 ml-3">
                <Tabs<ViewMode>
                  tabs={viewTabs}
                  selectedTab={viewMode}
                  setSelectedTab={setViewMode}
                  isSmall
                  noGap
                />
              </div>
            </div>
          )}

          {/* Signal Sections or Company Sections */}
          {viewMode === 'company' ? (
            <div className="space-y-2">
              {actionsByCompany.map((company) => (
                <CompanySection
                  key={company.companyId}
                  companyName={company.companyName}
                  companyId={company.companyId}
                  actions={company.actions}
                  isExpanded={expandedCompanies[company.companyId] ?? false}
                  onToggle={() => toggleCompany(company.companyId)}
                  onActionClick={handleActionClick}
                  onSentimentClick={handleSentimentClick}
                  getSentimentForAction={getSentimentForAction}
                />
              ))}
            </div>
          ) : (
            <div className="space-y-2">
              {SIGNAL_SECTIONS.map((section) => (
                <SignalSection
                  key={section.key}
                  title={section.title}
                  description={section.description}
                  Icon={section.Icon}
                  iconColor={section.iconColor}
                  isExpanded={signalSectionExpanded[section.key] ?? true}
                  onToggleExpanded={() => toggleSignalSection(section.key)}
                  actions={actionsBySignal[section.key] ?? []}
                  showAll={expandedSections[section.key]}
                  onToggleShowAll={() => toggleSection(section.key)}
                  signalType={section.key}
                  onActionClick={handleActionClick}
                  onSentimentClick={handleSentimentClick}
                  getSentimentForAction={getSentimentForAction}
                />
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="p-6 text-center">
          <p className="text-gray-500">
            No actions found. Your pipeline looks healthy!
          </p>
        </div>
      )}

      {/* Generation duration footer */}
      {summary.created && (
        <div className="px-4 py-2 border-t border-gray-100 text-xs text-gray-400 text-right">
          Updated{' '}
          {new Date(summary.created).toLocaleTimeString([], {
            hour: 'numeric',
            minute: '2-digit',
          })}
        </div>
      )}
    </div>
  )
}
